import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppNotification, PAYMENT_REQUEST_NOTIFICATION } from '../../core/models/notification';
import { NotificationStreamService } from '../../core/services/notification-stream-service';

/**
 * Transient popups for notifications arriving while the portal is open.
 *
 * Driven by {@link NotificationStreamService.arrivals$}, which emits once per previously
 * unseen notification and stays quiet during the initial load — without that, the unread
 * backlog the server replays on every reconnect would pop a stack of toasts every few minutes.
 *
 * Nothing here marks anything read: a role broadcast is one shared document on the server, so
 * a toast appearing on one admin's screen must not clear it from everybody else's bell.
 */
@Component({
  selector: 'app-notification-toast',
  templateUrl: './notification-toast.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class NotificationToast implements OnInit, OnDestroy {
  /** Long enough to read a sentence, short enough not to sit over the UI. */
  private static readonly VISIBLE_MS = 8_000;

  /** Oldest drops off rather than letting a burst cover the screen. */
  private static readonly MAX_VISIBLE = 3;

  protected toasts: AppNotification[] = [];

  private subscription: Subscription | null = null;
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(
    private readonly notificationStreamService: NotificationStreamService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.subscription = this.notificationStreamService.arrivals$.subscribe((notification) =>
      this.show(notification),
    );
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    for (const timer of this.timers.values()) clearTimeout(timer);
    this.timers.clear();
  }

  /** Opens what the notification is about, and marks it read because that is a real click. */
  protected open(notification: AppNotification) {
    this.dismiss(notification);
    this.notificationStreamService.markAsRead(notification);

    if (notification.type === PAYMENT_REQUEST_NOTIFICATION && notification.referenceId) {
      this.router.navigate(['/payments', notification.referenceId]).then();
    }
  }

  /** Removes the toast only. The notification stays unread in the bell. */
  protected dismiss(notification: AppNotification) {
    this.toasts = this.toasts.filter((t) => t.id !== notification.id);

    const timer = this.timers.get(notification.id);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.timers.delete(notification.id);
    }

    this.cdr.detectChanges();
  }

  private show(notification: AppNotification) {
    // a reconnect can re-deliver an id already on screen; refreshing beats stacking a copy
    if (this.toasts.some((t) => t.id === notification.id)) return;

    const next = [...this.toasts, notification];
    // clear the timers of any pushed off the end, or they would fire against a toast that is
    // already gone and leak until the component is destroyed
    for (const dropped of next.slice(0, Math.max(0, next.length - NotificationToast.MAX_VISIBLE))) {
      const timer = this.timers.get(dropped.id);
      if (timer !== undefined) {
        clearTimeout(timer);
        this.timers.delete(dropped.id);
      }
    }
    this.toasts = next.slice(-NotificationToast.MAX_VISIBLE);

    this.timers.set(
      notification.id,
      setTimeout(() => this.dismiss(notification), NotificationToast.VISIBLE_MS),
    );

    this.cdr.detectChanges();
  }
}
